// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useRef, useState } from 'react';
import styles from './styles';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';

export default function PopupWindow({
  x,
  y,
  attributes,
  index,
  totalCount,
  onPrev,
  onNext,
  onClose,
  parentRef,
  view,
  isLas
}) {
  const s = styles('', {}, 0, false, false, false);

  const popupRef = useRef<HTMLDivElement | null>(null);


  const lasLayerRef = useRef<GraphicsLayer | null>(null);


  const [position, setPosition] = useState({ left: x, top: y, transform: '' });


  const dragging = useRef(false);
  const hasDragged = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });


  useEffect(() => {
    if (!view) return;

    let lasLayer = view.map.findLayerById('highlight-las-layer');

    if (!lasLayer) {
      lasLayer = new GraphicsLayer({ id: 'highlight-las-layer', visible: false });
      view.map.add(lasLayer);
    }
    lasLayerRef.current = lasLayer;
  }, [view]);

  useEffect(() => {
    const lasLayer = lasLayerRef.current;

    if (!lasLayer) return;

    if (isLas) {
      lasLayer.visible = true;
    }
  }, [isLas]);

  useEffect(() => {
    if (!attributes?.geometry || !view) return;

    const geom = attributes.geometry;

    const lasLayer = lasLayerRef.current;

    let activeLayer = null;

    if (isLas) {
      activeLayer = lasLayer;
    } else {
      return;
    }

    if (!activeLayer) return;

    activeLayer.removeAll();

    let symbol;

    if (geom.type === 'polygon') {
      symbol = {
        type: 'simple-fill',
        outline: { color: [0, 255, 255, 1], width: 2 }
      };
    } else if (geom.type === 'polyline') {
      symbol = {
        type: 'simple-line',
        color: [0, 255, 255, 1],
        width: 3
      };
    } else {
      symbol = {
        type: 'simple-marker',
        color: [0, 255, 255, 1],
        size: 10,
        outline: { color: [255, 255, 255], width: 1 }
      };
    }
    activeLayer.add(new Graphic({ geometry: geom, symbol }));
  }, [attributes, isLas, view]);


  useEffect(() => {
    if (!popupRef.current) return;

    const parent = popupRef.current.offsetParent;
    if (!parent) return;

    const parentRect = parent.getBoundingClientRect();
    const popupRect = popupRef.current.getBoundingClientRect();

    const clampedX = Math.max(0, Math.min(x, parentRect.width - popupRect.width));
    const clampedY = Math.max(0, Math.min(y, parentRect.height - popupRect.height));

    hasDragged.current = false;
    dragging.current = false;

    setPosition({
      left: clampedX,
      top: clampedY,
      transform: 'translate(0,0)',
    });
  }, [x, y]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!popupRef.current) return;

    dragging.current = true;
    hasDragged.current = true;

    const popupRect = popupRef.current.getBoundingClientRect();

    dragOffset.current = {
      x: e.clientX - popupRect.left,
      y: e.clientY - popupRect.top,
    };

    e.preventDefault();
  };


  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging.current || !popupRef.current) return;
    if (!parentRef) return;

    const parentRect = popupRef.current.offsetParent.getBoundingClientRect();
    const popupRect = popupRef.current.getBoundingClientRect();

    let newX = e.clientX - parentRect.left - dragOffset.current.x;
    let newY = e.clientY - parentRect.top - dragOffset.current.y;

    newX = Math.max(0, Math.min(newX, parentRect.width - popupRect.width));
    newY = Math.max(0, Math.min(newY, parentRect.height - popupRect.height));

    setPosition({
      left: newX,
      top: newY,
      transform: 'translate(0,0)',
    });
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', () => (dragging.current = false));

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', () => (dragging.current = false));
    };
  }, []);

  const rows = Object.keys(attributes)
    .filter(
      (key) =>
        ![
          'objectid',
          'gid',
          'geometry',
          'layer_id',
          'origin_epsg',
          'srid',
          'site_id',
          'local_layer_id',
          'timestamp',
          'block_def_id',
          'shape',
          'st_area(shape)',
          'st_perimeter(shape)',
          'layerName',
          'layerColor',
          'st_length(shape)'
        ].includes(key)
    )
    .map((key, i) => (
      <tr key={key} style={s.popupTableRow(i)}>
        <td style={s.popupTableCellKey}>{key}</td>
        <td style={s.popupTableCellValue}>{attributes[key]}</td>
      </tr>
    ));

  return (
    <div
      ref={popupRef}
      style={s.popupContainer(position)}
    >
      <div style={{ ...s.popupHeader }} onMouseDown={handleMouseDown}>
        <div style={s.popupLayerInfo}>
          <div style={s.layerColorBox(attributes.layerColor)}></div>
          <span>{attributes.layerName || 'Feature'}</span>
        </div>

        <div style={s.popupControls}>
          <button disabled={index === 0} onClick={onPrev} style={s.prev_btn}>‹</button>
          <span>{index + 1} of {totalCount}</span>
          <button disabled={index === totalCount - 1} onClick={onNext} style={s.next_btn}>›</button>
          <span
            style={s.popupClose}
            onClick={() => {
              lasLayerRef.current?.removeAll();
              onClose();
            }}
          >
            ×
          </span>
        </div>
      </div>

      <div style={s.popupContentWrapper}>
        <table style={s.popupTable}>
          <tbody>{rows}</tbody>
        </table>
      </div>
    </div>
  );
}
